import { AppModule } from "@/enums/AppModule";
import {
  IPracticeDetails,
  IUserPractice,
  IUserSite,
  PracticeAssignment,
} from "./PracticeAssignment";
import { Site } from "./Site";
import { SiteAssignment } from "./SiteAssignment";
import { SpecialtyType } from "./SpecialtyType";

export class User {
  public id?: number;
  public adoId?: string;
  public corporateEntityId?: number;
  public isActive?: boolean;
  public contactEmail?: string;
  public firstName?: string;
  public lastName?: string;
  public phoneNumber?: string;
  public practiceAssignments?: PracticeAssignment[];
  public siteAssignments?: SiteAssignment[];
  public userRoles?: UserRole[];
  public userPractices?: IUserPractice[];
  public rolesPermissions?: IRolesPermission[];
  private modulePracticesMap: Partial<Record<AppModule, IUserPractice[]>>;
  private moduleList: AppModule[];
  private superUser?: boolean; // TODO: remove this after testing with super user role
  constructor(
    id?: number,
    adoId?: string,
    corporateEntityId?: number,
    isActive?: boolean,
    contactEmail?: string,
    firstName?: string,
    lastName?: string,
    phoneNumber?: string,
    practiceAssignments?: PracticeAssignment[],
    siteAssignments?: SiteAssignment[],
    userRoles?: UserRole[],
    rolesPermissions?: IRolesPermission[],
    userPracticesOverride?: IUserPractice[],
    modulePracticesMap?: Partial<Record<AppModule, IUserPractice[]>>,
    superUser?: boolean,
  ) {
    this.id = id;
    this.adoId = adoId;
    this.corporateEntityId = corporateEntityId;
    this.isActive = isActive;
    this.contactEmail = contactEmail;
    this.firstName = firstName;
    this.lastName = lastName;
    this.phoneNumber = phoneNumber;
    this.practiceAssignments = practiceAssignments;
    this.siteAssignments = siteAssignments;
    this.userRoles = userRoles;
    if (userPracticesOverride !== undefined) {
      this.userPractices = userPracticesOverride;
    } else if (practiceAssignments && siteAssignments) {
      this.userPractices = getUserRelatedPractices(
        practiceAssignments,
        siteAssignments,
      );
    } else {
      this.userPractices = [];
    }
    this.rolesPermissions = rolesPermissions;
    this.modulePracticesMap = modulePracticesMap ?? {};
    this.moduleList = Object.entries(this.modulePracticesMap)
      .filter(([, practices]) => (practices?.length ?? 0) > 0)
      .map(([module]) => module as AppModule);
    this.superUser = superUser;
  }

  public getDisplayName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  //TODO have to update based on role Id
  public isPharmacistRole(): boolean {
    return this.userRoles?.[0]?.role?.name === "Pharmacist";
  }

  public hasPermission(permissionId: number): boolean {
    return (
      this.rolesPermissions?.some(
        (permission) => permission.permissionId === permissionId,
      ) ?? false
    );
  }

  public getPracticesByModule(module: AppModule): IUserPractice[] {
    return this.modulePracticesMap[module] ?? [];
  }

  public getAvailableModules(): AppModule[] {
    return this.moduleList;
  }

  public isSuperUserRole(): boolean {
    return this.superUser ?? false;
  }
}

export class UserRole {
  public role: Role;
  constructor(role: Role) {
    this.role = role;
  }
}
export class Role {
  public isAllyOnly: boolean;
  public name: string;
  constructor(isAllyOnly: boolean, name: string) {
    this.isAllyOnly = isAllyOnly;
    this.name = name;
  }
}

export interface ICorporateEntityAssignment {
  userId: number;
  corpEntityId: number;
}

export interface IRolesPermission {
  permissionId: number;
  name?: string;
  categoryId?: number;
  categoryName?: string;
  isInternal?: boolean;
}

export interface IRole {
  id: number;
  applicationId: number;
  name: string;
  isActive: boolean;
  isAllyOnly: boolean;
  application: {
    name: string;
    isActive: boolean;
  };
  rolesPermissions: IRolesPermission[];
  isSelected?: boolean;
  usersCount?: number;
  corporateEntityId?: number;
}

export interface IUserRole {
  id: number;
  userId: number;
  roleId: number;
  isActive: boolean;
  role: IRole;
}

// private methods to extract practice and sites from practice and site assignments of the user.
const getUserRelatedPractices = (
  practiceAssignments: PracticeAssignment[],
  siteAssignments: SiteAssignment[],
): IUserPractice[] => {
  return practiceAssignments
    .map((pa) => {
      const practice: IUserPractice = {
        practiceId: pa?.practiceId ?? 0,
        practiceName:
          (pa?.practiceDetails &&
            (pa?.practiceDetails[0]?.displayName ??
              pa?.practiceDetails[0]?.name)) ??
          "",
        isActive: pa?.isActive ?? true,
        practiceDetail:
          (pa?.practiceDetails && pa?.practiceDetails[0]) ??
          ({} as IPracticeDetails),
      };
      practice.practiceSites = getPracticeRelatedSites(
        siteAssignments ?? [],
        practice,
      );
      return practice;
    })
    .filter((practice) => practice !== undefined)
    .sort((a, b) => a.practiceName.localeCompare(b.practiceName));
};

const getPracticeRelatedSites = (
  siteAssignments: SiteAssignment[],
  practice: IUserPractice,
): IUserSite[] =>
  siteAssignments
    .filter(
      (sa) =>
        sa.site &&
        sa.site.siteDetail &&
        sa.practiceId === practice.practiceId &&
        sa.siteId !== undefined,
    )
    .map((sa) => {
      const siteDetail = sa.site?.siteDetail;
      if (!siteDetail || !sa.site) {
        return null;
      }
      return {
        siteId: sa.siteId ?? 0,
        siteName: siteDetail?.displayName ?? siteDetail.name ?? "",
        displayName: siteDetail?.displayName ?? "",
        name: siteDetail?.name ?? "",
        isActive: sa.isActive ?? true,
        practiceId: siteDetail?.practiceId,
        practiceName: practice.practiceName ?? "",
        specialtyType: sa.site.specialtyType ?? ({} as SpecialtyType),
        site: sa.site ?? ({} as Site),
      };
    })
    .filter((site): site is IUserSite => site !== null)
    .sort((a, b) => a.siteName.localeCompare(b.siteName));
